import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

countries.registerLocale(en);

const LOCALE = 'en';

let masterCountryNames = null;
let countryByNormalizedName = null;

export function normalizeCountryName(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ');
}

function ensureCountryLookups() {
  if (masterCountryNames && countryByNormalizedName) {
    return;
  }

  const names = Object.values(countries.getNames(LOCALE));
  masterCountryNames = names.sort((a, b) => a.localeCompare(b));
  countryByNormalizedName = new Map(
    masterCountryNames.map((name) => [normalizeCountryName(name), name]),
  );
}

export function getMasterCountryList() {
  ensureCountryLookups();
  return masterCountryNames;
}

export function resolveCanonicalCountry(value) {
  const text = String(value ?? '').trim();
  if (!text) return null;

  ensureCountryLookups();

  const normalized = normalizeCountryName(text);
  const directMatch = countryByNormalizedName.get(normalized);
  if (directMatch) return directMatch;

  const alpha2 = countries.getAlpha2Code(text, LOCALE)
    || countries.getAlpha2Code(
      text.replace(/\b\w/g, (char) => char.toUpperCase()),
      LOCALE,
    );
  if (alpha2) {
    return countries.getName(alpha2, LOCALE) || null;
  }

  return null;
}

export function buildPersonaCountryOptions(personaCountries = []) {
  const options = new Set();

  personaCountries.forEach((value) => {
    const canonical = resolveCanonicalCountry(value);
    if (canonical) {
      options.add(canonical);
    }
  });

  return Array.from(options).sort((a, b) => a.localeCompare(b));
}

export function filterMasterCountries(search = '') {
  const query = normalizeCountryName(search);
  const allCountries = getMasterCountryList();

  if (!query) {
    return allCountries;
  }

  return allCountries.filter((country) =>
    normalizeCountryName(country).includes(query),
  );
}

export function sanitizeCountryValues(values = []) {
  const sanitized = [];
  const rejected = [];

  values.forEach((value) => {
    const canonical = resolveCanonicalCountry(value);
    if (canonical) {
      if (!sanitized.includes(canonical)) {
        sanitized.push(canonical);
      }
      return;
    }

    const text = String(value ?? '').trim();
    if (text) {
      rejected.push(text);
    }
  });

  return { sanitized, rejected };
}
